import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { AppError } from '@mews/framework';
import { Configuration } from './schema';

export type AppConfiguration = Configuration['configuration'] & {
    version: string;
    nowUtc: string | null;
};

type UpdateAppConfigParams = Pick<Partial<AppConfiguration>, 'platformVersion' | 'nowUtc'>;

interface AppConfigurationStore {
    config: AppConfiguration | null;
    setConfig: (config: AppConfiguration) => void;
    updateConfig: (config: UpdateAppConfigParams) => void;
}

const appConfigurationStore = createStore<AppConfigurationStore>((set) => ({
    config: null,
    setConfig: (config) => set({ config }),
    updateConfig: (config) =>
        set((state) => {
            if (state.config == null) throw new AppError('App configuration is not initialized');
            return { ...state, config: { ...state.config, ...config } };
        }),
}));

export const setAppConfiguration = appConfigurationStore.getState().setConfig;
export const updateAppConfiguration = appConfigurationStore.getState().updateConfig;

export const useAppConfiguration = () => {
    const config = useStore(appConfigurationStore, (store) => store.config);
    if (config == null) {
        throw new AppError(`App configuration is not initialized`);
    }
    return config;
};

/** Only use in special cases outside of react, e.g. while migrating legacy redux selectors
 * @deprecated useAppConfiguration() */
export const getAppConfiguration = () => {
    const { config } = appConfigurationStore.getState();
    if (config == null) {
        throw new AppError(`App configuration is not initialized`);
    }
    return config;
};
